import { useRef, useState } from "react";
import { Camera, Image as ImageIcon, X, Loader2 } from "lucide-react";

/**
 * PhotoPicker — universal phone-friendly photo picker.
 *
 * - On mobile (iOS Safari + Android Chrome) tapping "Add photo" shows the
 *   native sheet: Take Photo / Photo Library. We don't force `capture` so
 *   users can pick from their existing gallery.
 * - On desktop it's a normal file picker.
 * - URL paste was removed per user request — photos must be uploaded from
 *   the device, never pasted as a link (security + spam control).
 *
 * Backend: posts the binary as multipart/form-data to /api/uploads/image
 * (already exists in server.py — 8 MB cap, returns { url }).
 *
 * Props:
 *   value      — current image URL (parent owns state)
 *   onChange   — (url|null) => void
 *   kind       — "avatar" | "community" | "listing"  (server accepts these)
 *   testid     — optional data-testid prefix
 *   apiBase    — REACT_APP_BACKEND_URL/api (must be passed in by caller)
 */
export default function PhotoPicker({
  value,
  onChange,
  kind = "community",
  apiBase,
  testid = "photo-picker",
  label = "Add a photo",
}) {
  const inputRef = useRef(null);
  const cameraRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState("");

  const handleFile = async (file) => {
    if (!file) return;
    if (!file.type?.startsWith("image/")) {
      setErr("Please choose an image file.");
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      setErr("Image is over the 8 MB limit. Try a smaller one.");
      return;
    }
    setErr("");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("kind", kind);
      const r = await fetch(`${apiBase}/uploads/image`, { method: "POST", body: fd });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        throw new Error(d.detail || "Upload failed");
      }
      const d = await r.json();
      onChange?.(d.url || d.path);
    } catch (e) {
      setErr(e.message || "Couldn't upload that photo");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  if (value) {
    return (
      <div className="relative inline-block" data-testid={`${testid}-preview`}>
        <img src={value} alt="" className="rounded-lg max-h-44 border border-slate-200" />
        <button
          type="button"
          data-testid={`${testid}-remove`}
          onClick={() => onChange?.(null)}
          className="absolute -top-2 -right-2 w-7 h-7 rounded-full bg-white shadow-md border border-slate-200 flex items-center justify-center hover:bg-rose-50 hover:border-rose-300"
          aria-label="Remove photo"
        >
          <X className="w-4 h-4 text-slate-700" />
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-2" data-testid={testid}>
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          data-testid={`${testid}-take`}
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg border border-emerald-200 bg-emerald-50 text-emerald-700 text-sm font-semibold hover:bg-emerald-100 disabled:opacity-60"
        >
          {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />}
          {uploading ? "Uploading…" : label}
        </button>
        {/* Direct camera-only button — the rear camera opens instantly
            on iOS/Android when this input is tapped (capture=environment). */}
        <button
          type="button"
          data-testid={`${testid}-camera`}
          onClick={() => cameraRef.current?.click()}
          disabled={uploading}
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg border border-slate-200 text-slate-700 text-sm font-semibold hover:bg-slate-50 disabled:opacity-60 sm:hidden"
        >
          <Camera className="w-4 h-4" /> Take a photo
        </button>
      </div>

      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        // No `capture` attribute — lets the OS show both camera + gallery.
        onChange={(e) => handleFile(e.target.files?.[0])}
        className="hidden"
        data-testid={`${testid}-input`}
      />
      <input
        ref={cameraRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={(e) => handleFile(e.target.files?.[0])}
        className="hidden"
        data-testid={`${testid}-camera-input`}
      />

      <p className="text-xs text-slate-400 flex items-center gap-1">
        <ImageIcon className="w-3 h-3" /> JPG, PNG, HEIC up to 8 MB. Camera or library — tap to choose.
      </p>
      {err && <div data-testid={`${testid}-error`} className="text-sm text-rose-600">{err}</div>}
    </div>
  );
}
